import "./App.css";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { Navigate, Outlet, createBrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "./store/Store";
import { Suspense, lazy, memo } from "react";
import ErrorBoundary, { RouteErrorFallback } from "./components/ErrorBoundary";
import ThemeProviderWrapper from "./theme";
import Testmodus from "./components/Testmodus";
import WeergaveRegelaar from "./components/WeergaveRegelaar";
import Webshopontwerp from "./components/Webshopontwerp";

// ─────────────────────────────────────────────────────────────────────────────
// Lazy Loading Components for Code Splitting
// ─────────────────────────────────────────────────────────────────────────────

// Critical - Load immediately
import ConditionalRedirect from "./widgets/horecaWidget/ConndtionalRedirect";

// Lazy load all other components
const WidgetDynamicLoading = lazy(() => import("./widgets/WidgetDynamicLoading"));
const BookNowForm = lazy(() => import("./widgets/horecaWidget/BooknowForm"));
const Paymentfailure = lazy(() => import("./widgets/horecaWidget/Paymentfailure"));
const PaymentPending = lazy(() => import("./widgets/horecaWidget/PaymentPending"));
const SuccessScreen = lazy(() => import("./widgets/feedbackWidget/SuccessScreen"));
const OpeningHoursCalendar = lazy(() => import("./widgets/openingHours/OpeningHours"));
const ReservationCancelResponse = lazy(() => import("./widgets/horecaWidget/ReservationCancelResponse"));
const BookNowFormTicket = lazy(() => import("./widgets/ticketingWidget/BookNowFormTicket"));
const BookNowActivities = lazy(() => import("./widgets/ActivitiesWidget/BookNowActivities"));
const PaymentSuccessActivity = lazy(() => import("./widgets/ActivitiesWidget/PaymentSuccessActivity"));
const CancelReservation = lazy(() => import("./widgets/horecaWidget/CancelReservation"));

// Webshop components
const MenuComponent = lazy(() => import("./widgets/WebshopWidget/MenuComponent"));
const MenuItemListComponent = lazy(() => import("./widgets/WebshopWidget/MenuItemListComponent"));
const AddToCart = lazy(() => import("./widgets/WebshopWidget/AddToCart"));
const RecommendationProduct = lazy(() => import("./widgets/WebshopWidget/RecommendationProduct"));
const Cartdetails = lazy(() => import("./widgets/WebshopWidget/CartDetail"));
const Checkout = lazy(() => import("./widgets/WebshopWidget/Checkout"));
const ProductSetPage = lazy(() => import("./widgets/WebshopWidget/ProductSetPage"));
const PaymentStatusPage = lazy(() => import("./widgets/WebshopWidget/PaymentStatusPage"));

// Other widgets
const BooknowSalon = lazy(() => import("./widgets/salonWidget/BooknowSalon"));

// TV widgets
const TVWidgetDineIn = lazy(() => import("./widgets/TV/TVWidgetDineIn"));
const TVWidgetPickup = lazy(() => import("./widgets/TV/TVWidgetPickup"));
const TVWidgetBoth = lazy(() => import("./widgets/TV/TVWidgetBoth"));
const Login = lazy(() => import("./widgets/auth/Login"));
const SelectDashboardType = lazy(() => import("./widgets/auth/SelectDashboardType"));

// ─────────────────────────────────────────────────────────────────────────────
// Loading Fallback Component
// ─────────────────────────────────────────────────────────────────────────────
const LoadingFallback = memo(() => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100vh",
      backgroundColor: "#f5f5f5",
    }}
  >
    <CircularProgress size={50} />
  </Box>
));
LoadingFallback.displayName = "LoadingFallback";


// ─────────────────────────────────────────────────────────────────────────────
// Query Parameter Validation (Memoized)
// ─────────────────────────────────────────────────────────────────────────────
const hasValidQueryParams = (search) => {
  if (!search) return false;
  const params = new URLSearchParams(search);
  const widgetSetupId = params.get("widgetsetupid");
  const key = params.get("key");
  const resType = params.get("resType");
  const secretKey = params.get("secretkey");
  const mainModuleId = params.get("mainModuleId");

  if (resType === "cancel") {
    return Boolean(key && resType);
  }
  if (resType === "feedback") {
    return Boolean(resType);
  }
  return Boolean(widgetSetupId && key && resType);
};

// ─────────────────────────────────────────────────────────────────────────────
// Widget Wrapper with Validation
// ─────────────────────────────────────────────────────────────────────────────
const WidgetWrapper = memo(() => {
  const search = window.location.search;

  return hasValidQueryParams(search) ? (
    <Suspense fallback={<LoadingFallback />}>
      <WidgetDynamicLoading />
    </Suspense>
  ) : (
    <Navigate to="/" />
  );
});
WidgetWrapper.displayName = "WidgetWrapper";

// ─────────────────────────────────────────────────────────────────────────────
// Main App Component (Memoized)
// ─────────────────────────────────────────────────────────────────────────────
const App = memo(() => {
  return (
    <>
      <CssBaseline />
      <ErrorBoundary>
        {/* Een winkel in de testfase: dicht voor gasten, met een balk voor de eigenaar. (Mollie blok 2) */}
        <Testmodus>
          <Outlet />
        </Testmodus>
        {/* Taal en live voorbeeld uit het tabblad Weergave in het dashboard. (golf 1A, 10-10-2026) */}
        <WeergaveRegelaar />
        {/* Het ontwerp uit de webshop-editor en de bewerkstand daarin. (13-10-2026) */}
        <Webshopontwerp />
      </ErrorBoundary>
    </>
  );
});
App.displayName = "App";

// ─────────────────────────────────────────────────────────────────────────────
// Router Configuration with Optimized Code Splitting
// ─────────────────────────────────────────────────────────────────────────────
export const router = createBrowserRouter(
  [
    {
      path: "/",
      errorElement: <RouteErrorFallback />,
      element: (
        <ErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            <Provider store={store}>
              <ThemeProviderWrapper>
                <App />
              </ThemeProviderWrapper>
            </Provider>
          </Suspense>
        </ErrorBoundary>
      ),

      children: [
        {
          path: "/",
          element: <ConditionalRedirect />,
        },
        {
          path: "widget",
          element: <WidgetWrapper />,
          children: [],
        },
        {
          path: "/successFeedback",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <SuccessScreen />
            </Suspense>
          ),
        },
        {
          path: "bookSalon",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <BooknowSalon />
            </Suspense>
          ),
        },
        {
          path: `/`,
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <PaymentSuccessActivity />
            </Suspense>
          ),
        },
        {
          path: "/ReservationCancelSuccess",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ReservationCancelResponse />
            </Suspense>
          ),
        },
        {
          path: "/openingHours/:key",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <OpeningHoursCalendar />
            </Suspense>
          ),
        },
        {
          path: "/bookNow",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <BookNowForm />
            </Suspense>
          ),
        },
        {
          path: "bookNowTickets",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <BookNowFormTicket />
            </Suspense>
          ),
        },
        {
          path: "bookNowActivity",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <BookNowActivities />
            </Suspense>
          ),
        },
        {
          path: "paymentStatusfail",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Paymentfailure />
            </Suspense>
          ),
        },
        {
          path: "paymentpending",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <PaymentPending />
            </Suspense>
          ),
        },
        {
          path: "cancelReservation",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <CancelReservation />
            </Suspense>
          ),
        },
        {
          path: "webshopMenuItem",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <MenuComponent />
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<LoadingFallback />}>
                  <MenuItemListComponent />
                </Suspense>
              ),
            },
            {
              path: "addtocart",
              element: (
                <Suspense fallback={<LoadingFallback />}>
                  <AddToCart />
                </Suspense>
              ),
            },
            {
              path: "recommendedProduct",
              element: (
                <Suspense fallback={<LoadingFallback />}>
                  <RecommendationProduct />
                </Suspense>
              ),
            },
            {
              path: "cartdetail",
              element: (
                <Suspense fallback={<LoadingFallback />}>
                  <Cartdetails />
                </Suspense>
              ),
            },
            {
              path: "checkout",
              element: (
                <Suspense fallback={<LoadingFallback />}>
                  <Checkout />
                </Suspense>
              ),
            },
            {
              path: "productset",
              element: (
                <Suspense fallback={<LoadingFallback />}>
                  <ProductSetPage />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "menu",
          index: true,
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <MenuItemListComponent />
            </Suspense>
          ),
        },
        {
          path: "addtocart",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <AddToCart />
            </Suspense>
          ),
        },
        {
          path: "recommendedProduct",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <RecommendationProduct />
            </Suspense>
          ),
        },
        {
          path: "cartdetail",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Cartdetails />
            </Suspense>
          ),
        },
        {
          path: "checkout",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Checkout />
            </Suspense>
          ),
        },
        {
          path: "productset",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ProductSetPage />
            </Suspense>
          ),
        },
        {
          path: "kisoMenuItem",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <MenuComponent />
            </Suspense>
          ),
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<LoadingFallback />}>
                  <MenuItemListComponent />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "webshopMenu",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <MenuItemListComponent />
            </Suspense>
          ),
        },
        {
          path: "payment-status",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <PaymentStatusPage />
            </Suspense>
          ),
        },
        {
          path: "tv/login",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Login />
            </Suspense>
          ),
        },
        {
          path: "tv/select-type",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <SelectDashboardType />
            </Suspense>
          ),
        },
        {
          path: "tv/dinein",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <TVWidgetDineIn />
            </Suspense>
          ),
        },
        {
          path: "tv/pickup",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <TVWidgetPickup />
            </Suspense>
          ),
        },
        {
          path: "tv/both",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <TVWidgetBoth />
            </Suspense>
          ),
        },
      ],
    },
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    },
  }
);

export default App;
